import React, { useState, useCallback, useMemo } from 'react';
import { HierarchyNode } from './HierarchyNode';
import { HierarchyContextMenu } from './HierarchyContextMenu';
import { HierarchyDragState, ContextMenuState } from '../../../core/types/hierarchy.types';
import { useSceneStore } from '../../store/sceneStore';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { readFile } from '../../services/FileSystemService';
import { loadImageDimensions } from '../inspector/PropertyEditors/TexturePropertyEditor';

interface HierarchyTreeProps {
  searchQuery: string;
}

export const HierarchyTree: React.FC<HierarchyTreeProps> = ({ searchQuery }) => {
  const { gameObjects, rootIds, clearSelection, reparent, select } = useSceneStore();

  const [dragState, setDragState] = useState<HierarchyDragState | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);

  // Filter root nodes based on search
  const filteredRootIds = useMemo(() => {
    if (!searchQuery) return [...new Set(rootIds)];

    const query = searchQuery.toLowerCase();
    const matchingIds = new Set<string>();

    // Find all objects that match the search
    const findMatches = (id: string): boolean => {
      const obj = gameObjects[id];
      if (!obj) return false;

      const nameMatches = obj.name.toLowerCase().includes(query);
      const childMatches = obj.childIds.some((childId) => findMatches(childId));

      if (nameMatches || childMatches) {
        matchingIds.add(id);
        return true;
      }
      return false;
    };

    rootIds.forEach((id) => findMatches(id));

    // Deduplicate to avoid React key warnings
    return [...new Set(rootIds)].filter((id) => matchingIds.has(id));
  }, [gameObjects, rootIds, searchQuery]);

  const handleDragStart = useCallback((id: string) => {
    setDragState({
      draggedId: id,
      targetId: null,
      position: 'inside',
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragState(null);
  }, []);

  const handleDragOver = useCallback(
    (targetId: string, position: 'before' | 'after' | 'inside') => {
      if (dragState && dragState.draggedId !== '__external__') {
        // Internal drag
        if (dragState.draggedId === targetId) return;

        // Prevent dropping a parent onto its descendant
        const isDescendant = (parentId: string, childId: string): boolean => {
          const parent = gameObjects[parentId];
          if (!parent) return false;
          if (parent.childIds.includes(childId)) return true;
          return parent.childIds.some((cid) => isDescendant(cid, childId));
        };

        if (isDescendant(dragState.draggedId, targetId)) return;

        setDragState({
          ...dragState,
          targetId,
          position,
        });
      } else {
        // External drag (e.g. from project panel)
        setDragState({
          draggedId: '__external__',
          targetId,
          position,
        });
      }
    },
    [dragState, gameObjects]
  );

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    if (!dragState || !dragState.targetId) {
      setDragState(null);
      return;
    }

    const { draggedId, targetId, position } = dragState;
    const targetObject = gameObjects[targetId];

    if (!targetObject) {
      setDragState(null);
      return;
    }

    let newParentId: string | null = null;
    let insertIndex: number | undefined;

    if (position === 'inside') {
      newParentId = targetId;
    } else {
      newParentId = targetObject.parentId;

      if (newParentId) {
        const parentChildren = gameObjects[newParentId].childIds;
        const targetIndex = parentChildren.indexOf(targetId);
        insertIndex = position === 'before' ? targetIndex : targetIndex + 1;
      } else {
        const targetIndex = rootIds.indexOf(targetId);
        insertIndex = position === 'before' ? targetIndex : targetIndex + 1;
      }
    }

    // Check for external asset drop (from project panel)
    if (draggedId === '__external__') {
      const assetId = e.dataTransfer.getData('text/plain');
      if (assetId) {
        const { assets } = useProjectPanelStore.getState();
        const asset = assets[assetId];
        if (asset && asset.type === 'prefab') {
          try {
            const content = await readFile(asset.path);
            const prefabData = JSON.parse(content);
            if (prefabData.gameObjects && prefabData.rootId) {
              const { instantiatePrefab } = useSceneStore.getState();
              instantiatePrefab(prefabData, newParentId, insertIndex);
            }
          } catch (err) {
            console.error('[Hierarchy] Failed to instantiate prefab:', err);
          }
        } else if (asset && asset.type === 'sprite') {
          // Drop sprite texture onto a target node
          const { updateComponent } = useSceneStore.getState();
          const spriteComp = targetObject.components.find(c => c.type === 'sprite-renderer');
          if (spriteComp) {
            updateComponent(targetId, spriteComp.id, { texturePath: asset.path });
            // Load dimensions if not already set
            const existingW = spriteComp.data?.textureWidth as number | undefined;
            const existingH = spriteComp.data?.textureHeight as number | undefined;
            if (!existingW || !existingH || existingW <= 0 || existingH <= 0) {
              loadImageDimensions(asset.path)
                .then(({ width: w, height: h }) => {
                  updateComponent(targetId, spriteComp.id, {
                    texturePath: asset.path,
                    textureWidth: w,
                    textureHeight: h,
                  });
                })
                .catch(() => {});
            }
          }
        } else if (asset && asset.type === 'spine') {
          // Drop spine asset onto a target node
          const { updateComponent } = useSceneStore.getState();
          const spineComp = targetObject.components.find(c => c.type === 'spine-renderer');
          if (spineComp) {
            updateComponent(targetId, spineComp.id, { spineAssetPath: asset.path });
          }
        }
      }
      setDragState(null);
      return;
    }

    // Internal hierarchy reparent
    reparent(draggedId, newParentId, insertIndex);
    setDragState(null);
  }, [dragState, gameObjects, rootIds, reparent]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      // Select the item if not already selected
      const { selectedIds } = useSceneStore.getState();
      if (!selectedIds.includes(id)) {
        select(id);
      }
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        targetId: id,
      });
    },
    [select]
  );

  const handleEmptyContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      targetId: null,
    });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleRenameStart = useCallback((id: string) => {
    setRenamingId(id);
  }, []);

  const handleRenameEnd = useCallback(() => {
    setRenamingId(null);
  }, []);

  const handleEmptyClick = useCallback(() => {
    clearSelection();
    setContextMenu(null);
  }, [clearSelection]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const { selectedIds, deleteSelected, copySelected, cutSelected, paste, duplicateGameObject } =
        useSceneStore.getState();

      if (renamingId) return; // Don't handle shortcuts while renaming

      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          if (selectedIds.length > 0) {
            e.preventDefault();
            deleteSelected();
          }
          break;
        case 'F2':
          if (selectedIds.length === 1) {
            e.preventDefault();
            setRenamingId(selectedIds[0]);
          }
          break;
        case 'c':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            copySelected();
          }
          break;
        case 'x':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            cutSelected();
          }
          break;
        case 'v':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            paste();
          }
          break;
        case 'd':
          if (e.ctrlKey || e.metaKey) {
            if (selectedIds.length === 1) {
              e.preventDefault();
              duplicateGameObject(selectedIds[0]);
            }
          }
          break;
        case 'a':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            useSceneStore.getState().selectAll();
          }
          break;
        case 'Escape': {
          const { focusedContainerId } = useSceneStore.getState();
          if (focusedContainerId) {
            useSceneStore.getState().exitContainer();
          } else {
            clearSelection();
          }
          break;
        }
      }
    },
    [renamingId, clearSelection]
  );

  // Handle external drops on empty tree area (root-level)
  const handleTreeDragOver = useCallback((e: React.DragEvent) => {
    // Allow drop for external drags (don't interfere with internal drags)
    if (!dragState || dragState.draggedId === '__external__') {
      e.preventDefault();
    }
  }, [dragState]);

  const handleTreeDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    const assetId = e.dataTransfer.getData('text/plain');
    if (!assetId) return;

    const { assets } = useProjectPanelStore.getState();
    const asset = assets[assetId];
    if (asset && asset.type === 'prefab') {
      try {
        const content = await readFile(asset.path);
        const prefabData = JSON.parse(content);
        if (prefabData.gameObjects && prefabData.rootId) {
          const { instantiatePrefab } = useSceneStore.getState();
          instantiatePrefab(prefabData, null);
        }
      } catch (err) {
        console.error('[Hierarchy] Failed to instantiate prefab:', err);
      }
    }
    setDragState(null);
  }, []);

  const isEmpty = filteredRootIds.length === 0;

  return (
    <div
      className="flex-1 overflow-auto focus:outline-none"
      onClick={handleEmptyClick}
      onContextMenu={handleEmptyContextMenu}
      onKeyDown={handleKeyDown}
      onDragOver={handleTreeDragOver}
      onDrop={handleTreeDrop}
      tabIndex={0}
    >
      {isEmpty ? (
        <div className="flex flex-col items-center justify-center h-full text-center px-4 py-8">
          {searchQuery ? (
            <>
              <p className="text-sm text-zinc-500">No objects found</p>
              <p className="text-xs text-zinc-600 mt-1">
                Try a different search term
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-zinc-500">No objects in scene</p>
              <p className="text-xs text-zinc-600 mt-1">
                Click + to create a new object
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="py-1" onClick={(e) => e.stopPropagation()}>
          {filteredRootIds.map((id) => {
            const obj = gameObjects[id];
            if (!obj) return null;
            return (
              <HierarchyNode
                key={id}
                gameObject={obj}
                depth={0}
                searchQuery={searchQuery}
                dragState={dragState}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onContextMenu={handleContextMenu}
                renamingId={renamingId}
                onRenameStart={handleRenameStart}
                onRenameEnd={handleRenameEnd}
              />
            );
          })}
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <HierarchyContextMenu
          contextMenu={contextMenu}
          onClose={handleCloseContextMenu}
          onRename={handleRenameStart}
        />
      )}
    </div>
  );
};
